import type { CloudResource, CloudType } from "../types/cloud";
import { myLocalStorage } from "../../../StorageHelper";

const CLOUD_PRIORITY: CloudType[] = ["GCP", "AWS", "AZURE"];

const STORAGE_KEYS: Record<CloudType, string> = {
  GCP: "gcpSelectedProjectId",
  AWS: "selectedAwsAccount",
  AZURE: "latestSubList",
};

export interface CloudSelection {
  cloud: CloudType | null;
  resource: CloudResource | null;
}

export const readStoredResourceId = (cloud: CloudType): string | null => {
  const raw = localStorage.getItem(STORAGE_KEYS[cloud]);
  if (!raw) return null;

  try {
    const wrapper = JSON.parse(raw);
    const value = wrapper && typeof wrapper === "object" && "value" in wrapper ? wrapper.value : wrapper;

    if (cloud === "AZURE") {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      return parsed?.azureSubscriptionId || null;
    }

    return typeof value === "string" ? value : null;
  } catch {
    return null;
  }
};

export const persistSelectedResource = (cloud: CloudType, resource: CloudResource): void => {
  if (cloud === "GCP") {
    myLocalStorage.setItem("gcpSelectedProjectId", resource.id);
    myLocalStorage.setItem("gcpSelectedProjectName", resource.name);
    return;
  }

  if (cloud === "AWS") {
    myLocalStorage.setItem("selectedAwsAccount", resource.id);
    myLocalStorage.setItem("selectedAwsAccountName", resource.name);
    return;
  }

  myLocalStorage.setItem(
    "latestSubList",
    JSON.stringify({ azureSubscriptionId: resource.id, subscriptionName: resource.name })
  );
};

export const resolveResourceForCloud = (
  cloud: CloudType,
  resources: CloudResource[]
): CloudResource | null => {
  if (!resources || resources.length === 0) return null;

  const storedId = readStoredResourceId(cloud);
  const resource = (storedId ? resources.find((item) => item.id === storedId) : null) || resources[0];

  persistSelectedResource(cloud, resource);

  return resource;
};

export const resolveCloudSelection = (
  cloudResources: Record<CloudType, CloudResource[]>,
  preferredCloud: CloudType | null
): CloudSelection => {
  const hasResources = (cloud: CloudType) => (cloudResources[cloud] || []).length > 0;

  const cloud =
    (preferredCloud && hasResources(preferredCloud) ? preferredCloud : null) ||
    CLOUD_PRIORITY.find(hasResources) ||
    null;

  if (!cloud) return { cloud: null, resource: null };

  return { cloud, resource: resolveResourceForCloud(cloud, cloudResources[cloud]) };
};
